import { useId, useState, type ReactNode } from 'react';
import { termOf } from '../terminology.js';
/*
 * 样式跟着组件走，**不靠别的页面顺手把它带进来**。
 *
 * `.infodot*` 那几条规则原本只由 `pages/TerminologyPage.tsx` 引入。
 * 生产构建把 CSS 并成一份，看起来没事；但那是「另一个页面恰好也在包里」换来的，
 * 一旦术语页被拆成异步块或被删掉，这个点就变成一个**没有样式但不报错**的字母 i。
 * 挂到业务页面上之后这条依赖必须断掉——CSS 重复引入 Vite 会去重，代价为零。
 */
import '../terminology.css';

/**
 * 名词右侧的小 i（C-167 A8）。
 *
 * ## 形态约束照 A8 的四条，一条不放宽
 *
 * - **固定在名词右侧**：`margin-inline-start`，永不换位
 * - **同一尺寸、同一灰度**：尺寸与颜色都在 `terminology.css` 里写死，不接受 props 调
 * - **不抢视觉**：默认灰、不加边框阴影；只有 hover / focus 时才变深
 * - **一次悬停一条，不做嵌套**：解释里的名词是纯文本，**不再挂 i**
 *
 * 尺寸与灰度**不开放为参数**是刻意的：一旦能调，各页面就会各调一版，
 * 于是「同一尺寸同一灰度」这条约束在代码里没有任何东西守着它。
 *
 * ## 查不到就不渲染
 *
 * `termOf` 查不到时返回 `null`——**不渲染一个点开是空的 i**。
 * 一个点开没内容的 i 比没有 i 更坏：它承诺了解释，然后不给。
 *
 * ## 键盘可达
 *
 * 用 `<button>` 而不是 `<span>`：悬停之外还要能 Tab 到、能回车打开。
 * 只做 hover 的提示对键盘用户等于不存在。
 *
 * ## 挂载处
 *
 * C-167 A8 那一轮只在术语页上自演示，**不挂业务页面**。
 * C-168 收口时挂上了第一处业务页面：运营统计的「校验状态」徽标右侧——
 * 那一页原本用两段正文解释「统计表不是 SSOT」，而**产品页面标识对象、不解释对象**，
 * 要解释的东西归这里。挂载处以后会变多，这一行只记「已经不是零处了」，不逐处列。
 */
export function InfoDot({ term }: { term: string }): ReactNode {
  const [open, setOpen] = useState(false);
  const id = useId();
  const entry = termOf(term);

  if (entry === undefined) return null;

  return (
    <span className="infodot-wrap">
      <button
        type="button"
        className="infodot"
        aria-label={`${term} 的解释`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
      >
        i
      </button>
      {open && (
        <span className="infodot-pop" id={id} role="tooltip">
          <strong>{entry.term}</strong>
          <span className="infodot-def">{entry.definition}</span>
          {entry.formula !== undefined && (
            <span className="infodot-formula">
              分子：{entry.formula.numerator}
              <br />
              分母：{entry.formula.denominator}
            </span>
          )}
          {entry.pitfall !== undefined && (
            <span className="infodot-pitfall">易错：{entry.pitfall}</span>
          )}
        </span>
      )}
    </span>
  );
}
